import { FC, ReactNode, useState, useCallback, useMemo, createContext, useContext } from 'react';
import { useCurrentUser } from '@hooks/users';
import { useCan } from '@new/casl/hooks/useCan';
import { useJoinVideoCallMutation as useJoinVideoCall, Photo } from '@teachur/http-client';
import { VideoCall, User } from '../types/VideoCall';
import { VideoCallSession } from '../new/features/video-call/components/VideoCallSession/VideoCallSession';

export interface VideoCallState {
  isOpen: boolean;
  videoCallId: string | undefined;
  isLoading: boolean;
  error: Error | null;
}

export interface VideoCallActions {
  openVideoCall: (videoCallId: string) => void;
  closeVideoCall: () => void;
  resetError: () => void;
  setVideoCallData: (videoCall: VideoCall, photos?: Photo[]) => void;
  setVideoCall: (videoCall: VideoCall | undefined) => void;
  setPhotos: (photos: Photo[] | ((prev: Photo[]) => Photo[])) => void;
}

export interface VideoCallSessionData {
  videoCall?: VideoCall;
  user?: User;
  photos: Photo[];
  courseId?: number;
  studentId?: number;
  currentUserId?: number;
  isInstructor: boolean;
  twilioToken?: string;
  isConfirmed: boolean;
}

export interface VideoCallContextValue {
  state: VideoCallState;
  actions: VideoCallActions;
  session: VideoCallSessionData;
}

const VideoCallContext = createContext<VideoCallContextValue | null>(null);

interface VideoCallProviderProps {
  children: ReactNode;
}

export const VideoCallProvider: FC<VideoCallProviderProps> = ({ children }) => {
  const { data: user } = useCurrentUser();
  const isInstructor = useCan('manage', 'CourseHearing');

  const joinMutation = useJoinVideoCall();

  const [state, setState] = useState<VideoCallState>({
    isOpen: false,
    videoCallId: undefined,
    isLoading: false,
    error: null,
  });

  const [videoCall, setVideoCall] = useState<VideoCall>();
  const [twilioToken, setTwilioToken] = useState<string>();
  const [photos, setPhotos] = useState<Photo[]>([]);

  const openVideoCall = useCallback(
    (videoCallId: string) => {
      if (!videoCallId) {
        console.error('[VideoCallProvider] ERROR: videoCallId is empty!');
        return;
      }

      setState((prev) => ({
        ...prev,
        isOpen: true,
        videoCallId,
        error: null,
      }));

      joinMutation.mutate(videoCallId, {
        onSuccess: (data) => {
          setVideoCall(data.videoCall);
          setTwilioToken(data.token);
          const student = (data.videoCall as VideoCall).student;
          if (student?.photos) {
            setPhotos(student.photos);
          }
        },
      });
    },
    [joinMutation]
  );

  const closeVideoCall = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
      videoCallId: undefined,
    }));
    setVideoCall(undefined);
    setPhotos([]);
  }, []);

  const resetError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  const setVideoCallData = useCallback((videoCall: VideoCall, photos?: Photo[]) => {
    setVideoCall(videoCall);
    if (photos) {
      setPhotos(photos);
    }
  }, []);

  const isConfirmed = useMemo(() => {
    if (!videoCall || !user) return false;
    if (!videoCall.course) return true;
    return isInstructor
      ? (videoCall.confirmedExpert ?? false)
      : (videoCall.confirmedStudent ?? false);
  }, [videoCall, user, isInstructor]);

  const session = useMemo<VideoCallSessionData>(
    () => ({
      videoCall,
      user,
      photos,
      courseId: videoCall?.course?.id,
      studentId: videoCall?.student?.id,
      currentUserId: user?.id,
      isInstructor,
      twilioToken,
      isConfirmed,
    }),
    [videoCall, user, photos, isInstructor, twilioToken, isConfirmed]
  );

  const value = useMemo<VideoCallContextValue>(
    () => ({
      state,
      actions: {
        openVideoCall,
        closeVideoCall,
        resetError,
        setVideoCallData,
        setVideoCall,
        setPhotos,
      },
      session,
    }),
    [state, openVideoCall, closeVideoCall, resetError, setVideoCallData, session]
  );

  return (
    <VideoCallContext.Provider value={value}>
      {children}

      {state.isOpen && state.videoCallId && (
        <VideoCallSession
          videoCallId={state.videoCallId}
          isOpen={state.isOpen}
          onClose={closeVideoCall}
        />
      )}
    </VideoCallContext.Provider>
  );
};

export const useVideoCallContext = () => {
  const context = useContext(VideoCallContext);

  if (!context) {
    throw new Error('useVideoCallContext must be used within VideoCallProvider');
  }

  const { state, actions, session } = context;

  return {
    isOpen: state.isOpen,
    videoCallId: state.videoCallId,
    isLoading: state.isLoading,
    error: state.error,
    openVideoCall: actions.openVideoCall,
    closeVideoCall: actions.closeVideoCall,
    resetError: actions.resetError,
    setVideoCallData: actions.setVideoCallData,
    setVideoCall: actions.setVideoCall,
    setPhotos: actions.setPhotos,
    ...session,
  };
};
